import {
  Activity,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronUp,
  Factory,
  FileBarChart2,
  Layers,
  LayoutGrid,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Settings as SettingsIcon,
  ShieldCheck,
  Trophy,
  Wrench,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useClerk } from '@clerk/clerk-react'
import { signOutWithMedia } from '@/app/useMediaSession'
import { cn } from '@/lib/cn'
import { Avatar } from '@/components/ui/Avatar'
import { Logo } from '@/components/ds/Logo'
import { Tooltip } from '@/components/ui/Tooltip'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { useChannels } from '@/api/endpoints/channels'
import { channelColor } from '@/lib/channelColor'
import { ACTIVE_JOB_STATUSES, useJobsFeed } from '@/api/endpoints/jobs'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useReorderable } from '@/hooks/useReorderable'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { ChannelGroup, ManageChannelsLink } from './ChannelGroup'
import { needsManageChannelsLink } from './manageChannels'
import styles from './Sidebar.module.scss'
import { projectsLinkSearch } from '@/features/projects/filters'

const WIRED_ROUTES = new Set<string>([
  '/',
  '/factory',
  '/jobs',
  '/scheduler',
  '/pools',
  '/safety',
  '/ranking',
  '/analytics',
  '/reports',
  '/tools',
])

interface NavItem {
  to: string
  label: string
  icon: typeof LayoutGrid
  /** Key used to look up a live badge count in `useJobsFeed`. */
  badge?: 'active-jobs'
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Projects', icon: LayoutGrid },
  { to: '/scheduler', label: 'Scheduler', icon: Calendar },
  { to: '/pools', label: 'Pools', icon: Layers },
  { to: '/jobs', label: 'Jobs', icon: Activity, badge: 'active-jobs' },
  { to: '/factory', label: 'Factories', icon: Factory },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/ranking', label: 'Ranking', icon: Trophy },
  { to: '/safety', label: 'Safety', icon: ShieldCheck },
  { to: '/reports', label: 'Reports', icon: FileBarChart2 },
  { to: '/tools', label: 'Tools', icon: Wrench },
]
// Channels e Settings não têm link aqui: Settings está no header e no menu do
// usuário, e Channels é a engrenagem do bloco de canais (ver ManageChannelsLink).

const NAV_BY_PATH = new Map(NAV_ITEMS.map((item) => [item.to, item]))

export interface SidebarProps {
  /**
   * - `fixed`: rendered as a fixed left rail on desktop. Supports the
   *   collapsed (icon-only) state via the localStorage toggle.
   * - `drawer`: rendered inside the AppLayout mobile drawer. Always
   *   full-width inside the drawer; collapse toggle and tooltips are
   *   suppressed (no room / no hover on touch).
   */
  variant?: 'fixed' | 'drawer'
  /**
   * Drawer-only: invoked when the user navigates to a different route
   * via a sidebar link. Used by AppLayout to close the drawer once the
   * user picks a destination.
   */
  onNavigate?: () => void
}

export function Sidebar({ variant = 'fixed', onNavigate }: SidebarProps) {
  const isDrawer = variant === 'drawer'
  const { data: channels = [], isPending: channelsPending } = useChannels()
  const { data: jobsFeed } = useJobsFeed()
  const { signOut } = useClerk()
  const currentUser = useCurrentUser()
  const activeJobsCount =
    jobsFeed?.data.filter((j) => ACTIVE_JOB_STATUSES.includes(j.status)).length ?? 0
  // Drawer always renders expanded — the mobile drawer has plenty of
  // room and the collapse toggle is suppressed there. Setter is still
  // wired (only called from the desktop collapse button branch below).
  const [collapsedPersisted, setCollapsed] = useLocalStorage<boolean>(
    'prism.sidebar.collapsed',
    false,
  )
  const collapsed = isDrawer ? false : collapsedPersisted
  // Drag a link to reorder the nav; the order lives in this browser only.
  const nav = useReorderable(
    'prism.sidebar.navOrder',
    NAV_ITEMS.map((item) => item.to),
    'y',
  )
  // Channel-list expansion is ephemeral on purpose — "trocar e seguir":
  // expand long enough to pick a new channel, then auto-collapse back to
  // the single-active-row view. Survives across renders within the same
  // mount but resets on full reload, which keeps the sidebar tight by
  // default even after the catalog grows.
  const [channelsExpanded, setChannelsExpanded] = useState(false)
  // The expanded list only behaves like a dropdown in mode 3 below
  // (full-width sidebar, >1 channel). In the other modes the list is the
  // permanent view and there is nothing to "close".
  const switcherOpen = !collapsed && channels.length > 1 && channelsExpanded
  const switcherRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!switcherOpen) return
    // Clicking anywhere outside the open list — page, nav, footer — closes
    // it, same as any dropdown. `pointerdown` (not `click`) so it also
    // catches presses that never complete a click, e.g. a drag that starts
    // outside. Clicks inside are handled by the rows themselves.
    const onPointerDown = (e: PointerEvent) => {
      const el = switcherRef.current
      if (el && e.target instanceof Node && !el.contains(e.target)) {
        setChannelsExpanded(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setChannelsExpanded(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [switcherOpen])

  // Channel switcher reads / writes the ``channel`` search param on the
  // CURRENT route so jumping channels doesn't yank the user back to
  // Projects (the bug we hit on Analytics: switching channel sent us to /
  // and the previous tab disappeared). All channel-aware pages
  // (`/`, `/safety`, `/ranking`, `/analytics`) consume the same param via
  // ``useActiveChannel``, so the switch is universal.
  const navigate = useNavigate()
  const currentSearch = useSearch({ strict: false })
  const channelFromSearch = (currentSearch as Record<string, unknown>)['channel']
  const channelFromUrl = typeof channelFromSearch === 'string' ? channelFromSearch : null
  // When the URL has no ``?channel=``, fall back to the default channel
  // (or the first one available) so the sidebar reflects what the
  // channel-aware pages are actually showing — they all delegate to
  // ``useActiveChannelFromUrl`` which already does this fallback.
  const fallbackChannel = channels.find((c) => c.is_default)?.slug ?? channels[0]?.slug ?? null
  const currentChannel = channelFromUrl ?? fallbackChannel
  // Search payload reused by every internal Link in the sidebar so the
  // channel context survives nav clicks. Routes that don't accept
  // ``channel`` strip it via their ``validateSearch``.
  const navSearch = currentChannel ? { channel: currentChannel } : {}
  // Projects leva também os filtros lembrados (ver ``projectsLinkSearch``).
  const projectsSearch = projectsLinkSearch(currentChannel)

  return (
    <aside
      className={cn(
        styles.sidebar,
        collapsed && styles.collapsed,
        isDrawer && styles.drawerVariant,
      )}
    >
      <div className={styles.logoRow}>
        <Link
          to="/"
          search={projectsSearch}
          className={styles.logoLink}
          aria-label="Prism · home"
          onClick={onNavigate}
        >
          <Logo variant={collapsed ? 'mark' : 'full'} size="md" />
        </Link>
        {!isDrawer ? (
          <Tooltip content={collapsed ? 'Expandir sidebar' : 'Minimizar sidebar'} side="right">
            <button
              type="button"
              className={styles.collapseBtn}
              onClick={() => {
                setCollapsed((c) => !c)
              }}
              aria-label={collapsed ? 'Expandir sidebar' : 'Minimizar sidebar'}
              aria-pressed={collapsed}
            >
              {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            </button>
          </Tooltip>
        ) : null}
      </div>

      <nav className={styles.nav} {...nav.containerProps}>
        {nav.orderedIds.map((to) => {
          const item = NAV_BY_PATH.get(to)
          if (!item) return null
          const Icon = item.icon
          const isDisabled = !WIRED_ROUTES.has(item.to)
          const badgeCount = item.badge === 'active-jobs' ? activeJobsCount : 0
          const link = (
            <Link
              key={item.to}
              to={item.to}
              search={item.to === '/' ? projectsSearch : navSearch}
              disabled={isDisabled}
              className={styles.navItem}
              onClick={onNavigate}
              // Match by pathname only (ignore search params — channel can
              // change without breaking the highlight). For "/", force
              // exact so the home doesn't claim "active" on every route
              // (every path starts with "/"). For deeper routes, allow
              // prefix so `/factory/viral-shorts` keeps Factory lit.
              activeOptions={{
                exact: item.to === '/',
                includeSearch: false,
              }}
              activeProps={{ className: cn(styles.navItem, styles.active) }}
              aria-disabled={isDisabled}
              {...nav.getItemProps(item.to)}
            >
              <Icon aria-hidden className={styles.navIcon} />
              <span className={styles.navLabel}>{item.label}</span>
              {badgeCount > 0 ? (
                <span className={styles.navBadge} aria-label={`${String(badgeCount)} ativo`}>
                  {badgeCount}
                </span>
              ) : null}
            </Link>
          )
          if (collapsed) {
            // Tooltip needs a unique key on the wrapper since Link's key
            // moves up.
            const tipLabel =
              badgeCount > 0 ? `${item.label} · ${String(badgeCount)} ativo` : item.label
            return (
              <Tooltip key={item.to} content={tipLabel} side="right">
                {link}
              </Tooltip>
            )
          }
          return link
        })}
      </nav>

      {/*
        Pinned bottom region. The channel switcher sits directly above
        the user footer — both stay glued to the bottom of the sidebar
        regardless of nav height. Channel switcher has 3 modes:

        1) Sidebar collapsed (icon-only) — preserve the legacy "all
           channel dots visible" layout: with only the colored bullet
           rendering anyway, the noise is minimal and tooltips do the
           rest.
        2) ≤1 channel — render the single ChannelGroup as before; no
           expand/collapse UI, the user has nowhere to switch to.
        3) Multi-channel + sidebar full-width — collapse to just the
           active row (with its platform label above), and let the
           user click to expand the full grouped list. Clicking any
           channel in the expanded list switches and re-collapses
           ("trocar e seguir"), so the sidebar stays tight.

        Sem o link Channels na navegação, a engrenagem do cabeçalho do grupo
        é o caminho até /channels. Nos dois casos sem cabeçalho (sidebar
        recolhido, conta ainda sem canal), o ManageChannelsLink faz esse papel.
      */}
      <div className={styles.bottomStack}>
        {(() => {
          const activeChannel = channels.find((c) => c.slug === currentChannel) ?? null
          const showCollapsedSwitcher =
            !collapsed && channels.length > 1 && !channelsExpanded && activeChannel != null
          const platforms = ['youtube', 'tiktok'] as const

          if (showCollapsedSwitcher) {
            const platformLabel = activeChannel.platform === 'tiktok' ? 'TikTok' : 'YouTube'
            return (
              <>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionLabel}>{platformLabel}</span>
                  <Link
                    to="/channels"
                    search={navSearch}
                    className={styles.sectionAction}
                    aria-label={`Gerenciar canais ${platformLabel}`}
                    title={`Gerenciar canais ${platformLabel}`}
                    onClick={onNavigate}
                  >
                    <SettingsIcon aria-hidden />
                  </Link>
                </div>
                <div className={styles.channels}>
                  <button
                    type="button"
                    className={cn(styles.channel, styles.channelActive)}
                    aria-current="true"
                    aria-expanded="false"
                    aria-label={`${activeChannel.name} · clique pra trocar`}
                    onClick={() => {
                      setChannelsExpanded(true)
                    }}
                  >
                    <span
                      className={styles.channelDot}
                      style={{ background: channelColor(activeChannel.slug) }}
                      aria-hidden
                    />
                    <span className={styles.channelName}>{activeChannel.name}</span>
                    <ChevronDown aria-hidden className={styles.channelToggle} />
                  </button>
                </div>
              </>
            )
          }

          // Expanded / fallback: full grouped list. Wrap in a scroll
          // wrapper so a fat catalog doesn't shove the user footer off
          // the bottom of the viewport.
          return (
            <div
              ref={switcherRef}
              className={cn(styles.channelsScrollWrapper, switcherOpen && styles.switcherOpen)}
            >
              {needsManageChannelsLink({
                collapsed,
                channelsSettled: !channelsPending,
                channelCount: channels.length,
              }) ? (
                <ManageChannelsLink
                  collapsed={collapsed}
                  navSearch={navSearch}
                  onNavigate={onNavigate}
                />
              ) : null}
              {platforms.map((platform) => {
                const platformChannels = channels.filter((c) => c.platform === platform)
                const label = platform === 'youtube' ? 'YouTube' : 'TikTok'
                return (
                  <ChannelGroup
                    key={platform}
                    label={label}
                    channels={platformChannels}
                    activeSlug={currentChannel}
                    collapsed={collapsed}
                    navSearch={navSearch}
                    onSelect={(slug) => {
                      if (slug !== currentChannel) {
                        void navigate({
                          to: '.',
                          search: (prev) => ({
                            ...(prev as Record<string, unknown>),
                            channel: slug,
                          }),
                        })
                      }
                      // Always re-collapse the switcher after a click — same
                      // channel just toggles back, different channel switches
                      // and tightens the sidebar.
                      setChannelsExpanded(false)
                      onNavigate?.()
                    }}
                  />
                )
              })}
            </div>
          )
        })()}

        {isDrawer ? (
          <DropdownMenuRoot>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(styles.footer, styles.footerButton)}
                aria-label="Menu do usuário"
              >
                <Avatar name={currentUser.name} src={currentUser.imageUrl ?? undefined} />
                <div className={styles.footerText}>
                  <div className={styles.footerName}>{currentUser.name}</div>
                  {currentUser.email ? (
                    <div className={styles.footerRole}>{currentUser.email}</div>
                  ) : null}
                </div>
                <ChevronUp aria-hidden className={styles.footerChevron} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="top"
              align="start"
              sideOffset={8}
              className={styles.userMenu}
            >
              <DropdownMenuItem
                leadingIcon={<SettingsIcon />}
                onSelect={() => {
                  void navigate({ to: '/settings', search: navSearch })
                  onNavigate?.()
                }}
              >
                Configurações
              </DropdownMenuItem>
              <DropdownMenuItem
                danger
                leadingIcon={<LogOut />}
                onSelect={() => {
                  // Don't pass a callback — App.tsx watches `isSignedIn` and
                  // invalidates the router, which redirects to /login. The
                  // media cookie goes first (card #277): Clerk reloads the page.
                  void signOutWithMedia(signOut)
                }}
              >
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenuRoot>
        ) : (
          <div className={styles.footer}>
            <Avatar name={currentUser.name} src={currentUser.imageUrl ?? undefined} />
            {!collapsed ? (
              <div className={styles.footerText}>
                <div className={styles.footerName}>{currentUser.name}</div>
                {currentUser.email ? (
                  <div className={styles.footerRole}>{currentUser.email}</div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </aside>
  )
}
